import React from "react";
import { styled } from "@mui/system";
import Box from "@vapor/react-material/Box";
import AppBar from "@vapor/react-material/AppBar";
import ToolbarMUI from "@mui/material/Toolbar";
import { ToolbarItem } from "./ToolbarItem";
import { ToolbarProps } from "@mui/material/Toolbar";

interface ToolbarPropsExtended extends ToolbarProps {
  size?: string;
}

declare module "@mui/material/Toolbar" {
  interface ToolbarPropsVariantOverrides {
    navigation: true;
    surface: true;
  }
}

export const Toolbar: (props: ToolbarPropsExtended) => JSX.Element = styled(
  React.forwardRef<HTMLDivElement, ToolbarPropsExtended>((props, ref) => {
    return <ToolbarMUI ref={ref} {...props} />;
  }),
  {
    name: "Toolbar",
    slot: "Root",
    overridesResolver: (props, styles) => []
  }
)<ToolbarPropsExtended>(({ theme }) => ({
  "&.MuiToolbar-root": {
    height: 48,
    background: theme.palette.primary.background,
    justifyContent: "space-between",
    paddingLeft: 16,
    paddingRight: 16
  },
  "&.MuiToolbar-root .MuiIconButton-root": {
    color: theme.palette.primary.iconInverse,
    ":hover": {
      background: theme.palette.primary.primary80Color,
      minHeight: 48,
      height: 48
    }
  }
}));

export type VaporToolbarProps = {
  withoutAppBar?: boolean;
} & VaporToolbarSimpleProps;

export const VaporToolbar = React.forwardRef(
  (
    {
      contentLeft = [],
      contentRight = [],
      contentCenter = [],
      variant = "regular",
      size = "medium",
      withoutAppBar = false
    }: VaporToolbarProps,
    ref: React.Ref<HTMLDivElement>
  ) => {
    return withoutAppBar ? (
      <Box
        sx={{
          position: "sticky",
          bottom: 0
        }}
        ref={ref}
      >
        <ToolbarSimple
          contentLeft={contentLeft}
          contentRight={contentRight}
          contentCenter={contentCenter}
          variant={variant}
          size={size}
        />
      </Box>
    ) : (
      <>
        <Toolbar
          size={size}
          variant="surface"
          sx={{
            "&.MuiToolbar-root": {
              background: "transparent"
            }
          }}
          ref={ref}
        ></Toolbar>
        <Box
          sx={{
            flexGrow: 1,
            position: "fixed",
            width: "stretch",
            "@media not all and (min-resolution:.001dpcm)": {
              width: "calc(100% - 48px)",
              "@media screen and (max-width: 1200px)": {
                width: "100%"
              }
            },
            bottom: 0,
            zIndex: 1100
          }}
        >
          <AppBar
            position="relative"
            sx={{
              "& .MuiPaper-root.MuiAppBar-root.AppBar-root": {
                position: "relative"
              }
            }}
          >
            <ToolbarSimple
              contentLeft={contentLeft}
              contentRight={contentRight}
              contentCenter={contentCenter}
              variant={variant}
              size={size}
            />
          </AppBar>
        </Box>
      </>
    );
  }
);

type VaporToolbarSimpleProps = {
  contentRight?: React.ReactNode[] | React.ReactNode;
  contentLeft?: React.ReactNode[] | React.ReactNode;
  contentCenter?: React.ReactNode[] | React.ReactNode;
  size?: "small" | "medium" | "large";
  variant?: "regular" | "surface";
};

const ToolbarSimple = ({
  contentLeft = [],
  contentRight = [],
  contentCenter = [],
  variant = "regular",
  size = "medium"
}: VaporToolbarSimpleProps) => {
  return (
    <Toolbar variant={variant} size={size}>
      <Box sx={{ display: "flex" }}>
        {contentLeft && Array.isArray(contentLeft) ? (
          contentLeft.map((item, index) => {
            return <ToolbarItem key={index}>{item}</ToolbarItem>;
          })
        ) : (
          <ToolbarItem>{contentLeft}</ToolbarItem>
        )}
      </Box>
      <Box sx={{ display: "flex" }}>
        {contentCenter && Array.isArray(contentCenter) ? (
          contentCenter.map((item, index) => {
            return <ToolbarItem key={index}>{item}</ToolbarItem>;
          })
        ) : (
          <ToolbarItem>{contentCenter}</ToolbarItem>
        )}
      </Box>
      <Box sx={{ display: "flex" }}>
        {contentRight && Array.isArray(contentRight) ? (
          contentRight.map((item, index) => {
            return <ToolbarItem key={index}>{item}</ToolbarItem>;
          })
        ) : (
          <ToolbarItem>{contentRight}</ToolbarItem>
        )}
      </Box>
    </Toolbar>
  );
};
